import { TellescopeModelFromName } from "@tellescope/tanstack-query";
import { FormResponseValue } from "@tellescope/types-models";
import { useCallback, useMemo, useState } from "react";
import { useStateWithFallback } from "@tellescope/react-hooks";
import {
  buildTreeFromFields,
  collectIdsToDropOnBack,
  countFormPages,
  countRemainingFormPages,
  isValueEmpty,
  resolveInitialNavState,
  resolveNextDisplayableField,
  subFieldIdsOf,
} from "../../utils";
import type { FormFieldNode, NextFieldLogicOptions } from "../../utils/types";
import type { LocalTellescopeFormResponseMap } from "../use-tellescope-form-core";

const hasNoActiveField = (field: FormFieldNode | undefined) => !field;

const percentageForPage = (pageIndex: number, totalPages: number) => ((pageIndex + 1) / totalPages) * 100;

export interface UseFormNavigationArgs {
  fieldsArr: TellescopeModelFromName<"form_fields">[] | undefined;
  fieldsMap: Record<string, TellescopeModelFromName<"form_fields">>;
  subFieldIds: Set<string>;
  currentResponses: LocalTellescopeFormResponseMap;
  responsesMap: Record<string, FormResponseValue>;
  logicOptions: NextFieldLogicOptions;
  existingResponses: FormResponseValue[] | undefined;
  startingFieldId: string | undefined;
  onBeforeAdvance: (activeField: FormFieldNode) => void;
}

export const useFormNavigation = ({
  fieldsArr,
  fieldsMap,
  subFieldIds,
  currentResponses,
  responsesMap,
  logicOptions,
  existingResponses,
  startingFieldId,
  onBeforeAdvance,
}: UseFormNavigationArgs) => {
  const fieldTree = useMemo(() => {
    if (!fieldsArr?.length) return undefined;
    return buildTreeFromFields(fieldsArr);
  }, [fieldsArr]);

  const existingResponseFieldIds = useMemo(
    () => new Set(existingResponses?.map((r) => r.fieldId) ?? []),
    [existingResponses]
  );

  const initialNavState = useMemo(() => {
    if (!fieldTree) return undefined;

    return resolveInitialNavState({
      fieldTree,
      fieldsMap,
      subFieldIds,
      answeredFieldIds: new Set(Object.keys(responsesMap)),
      existingResponseFieldIds,
      startingFieldId,
      walkContext: {
        responsesByFieldId: currentResponses,
        allResponses: Object.values(currentResponses).filter((r) => !isValueEmpty(r.answer?.value)),
        logicOptions,
      },
    });
  }, [
    fieldTree,
    fieldsMap,
    subFieldIds,
    currentResponses,
    logicOptions,
    responsesMap,
    startingFieldId,
    existingResponseFieldIds,
  ]);

  const [resolvedActiveField, setActiveField] = useStateWithFallback<FormFieldNode | undefined>({
    fallbackValue: initialNavState?.activeField,
    fallbackCondition: hasNoActiveField,
  });

  const includedFieldIdsFallback = useMemo(
    () => initialNavState?.included ?? new Set<string>(),
    [initialNavState?.included]
  );
  const [includedFieldIds, setIncludedFieldIds] = useStateWithFallback<Set<string>>({
    fallbackValue: includedFieldIdsFallback,
  });

  const prevFieldStackFallback = useMemo(() => initialNavState?.prevStack ?? [], [initialNavState?.prevStack]);
  // An empty stack is a real state — the user navigated back to the first page — so it must
  // stay distinguishable from "not yet seeded". Treating it as empty would resolve back to
  // the resume stack and report a stale currentPageIndex.
  const [resolvedPrevStack, setPrevFieldStack] = useStateWithFallback<FormFieldNode[]>({
    fallbackValue: prevFieldStackFallback,
  });

  const currentPageIndex = resolvedPrevStack.length;

  const totalPages = useMemo(() => {
    if (!fieldTree) return 1;

    return countFormPages(
      fieldTree,
      subFieldIds,
      {
        responsesByFieldId: currentResponses,
        allResponses: Object.values(currentResponses),
        logicOptions,
      },
      fieldsMap
    );
  }, [fieldTree, currentResponses, logicOptions, subFieldIds, fieldsMap]);

  const [navigatedProgress, setNavigatedProgress] = useState<number | undefined>(undefined);
  const progress = navigatedProgress ?? percentageForPage(currentPageIndex, totalPages);

  const { nextField, skippedHiddenFieldIds } = useMemo((): {
    nextField: FormFieldNode | undefined;
    skippedHiddenFieldIds: string[];
  } => {
    if (!resolvedActiveField) return { nextField: undefined, skippedHiddenFieldIds: [] };

    return resolveNextDisplayableField({
      activeField: resolvedActiveField,
      fieldsMap,
      subFieldIds,
      walkContext: {
        responsesByFieldId: currentResponses,
        allResponses: Object.values(currentResponses),
        logicOptions,
      },
    });
  }, [resolvedActiveField, currentResponses, logicOptions, subFieldIds, fieldsMap]);

  const goToNextFieldInternal = useCallback(() => {
    if (!resolvedActiveField || !nextField) return;

    onBeforeAdvance(resolvedActiveField);

    const subFieldIdsToInclude = subFieldIdsOf(resolvedActiveField.value);

    setPrevFieldStack((prev) => [...prev, resolvedActiveField]);
    setIncludedFieldIds((prev) => {
      const next = new Set(prev);
      subFieldIdsToInclude.forEach((id) => next.add(id));
      skippedHiddenFieldIds.forEach((id) => next.add(id));
      next.add(nextField.value.id);
      return next;
    });
    setActiveField(nextField);
    setNavigatedProgress(percentageForPage(currentPageIndex + 1, totalPages));
  }, [
    resolvedActiveField,
    nextField,
    skippedHiddenFieldIds,
    setIncludedFieldIds,
    setPrevFieldStack,
    setActiveField,
    onBeforeAdvance,
    currentPageIndex,
    totalPages,
  ]);

  const goToPreviousField = useCallback(() => {
    const stack = resolvedPrevStack;
    if (stack.length === 0) return;
    if (resolvedActiveField?.value?.options?.disableGoBack) return;

    const previous = stack[stack.length - 1];

    const idsToDrop = collectIdsToDropOnBack(resolvedActiveField, previous, {
      responsesByFieldId: currentResponses,
      allResponses: Object.values(currentResponses),
      logicOptions,
    });

    setIncludedFieldIds((prev) => new Set(Array.from(prev).filter((id) => !idsToDrop.has(id))));

    if (previous) {
      setPrevFieldStack(stack.slice(0, -1));
      setActiveField(previous);
      setNavigatedProgress(percentageForPage(currentPageIndex - 1, totalPages));
    }
  }, [
    resolvedActiveField,
    resolvedPrevStack,
    setIncludedFieldIds,
    setPrevFieldStack,
    setActiveField,
    currentResponses,
    logicOptions,
    currentPageIndex,
    totalPages,
  ]);

  const isPreviousDisabled = useMemo(() => {
    if (resolvedPrevStack.length === 0) return true;
    if (resolvedActiveField?.value?.options?.disableGoBack) return true;
    return false;
  }, [resolvedPrevStack.length, resolvedActiveField?.value?.options?.disableGoBack]);

  const showSubmit = useMemo(() => {
    if (!resolvedActiveField) return false;
    return resolvedActiveField.children.length === 0 || !nextField;
  }, [resolvedActiveField, nextField]);

  const getNumberOfRemainingPages = useCallback(() => {
    if (!resolvedActiveField) return 0;
    return countRemainingFormPages(resolvedActiveField.value, fieldsArr ?? [], subFieldIds);
  }, [resolvedActiveField, fieldsArr, subFieldIds]);

  return {
    resolvedActiveField,
    includedFieldIds,
    resolvedPrevStack,
    currentPageIndex,
    totalPages,
    progress,
    nextField,
    goToNextFieldInternal,
    goToPreviousField,
    isPreviousDisabled,
    showSubmit,
    getNumberOfRemainingPages,
  };
};
